import * as Sentry from "@sentry/react"

const RELOAD_ATTEMPT_KEY = "chunk_reload_attempt"
const MAX_RELOAD_ATTEMPTS = 2
const RELOAD_ATTEMPT_EXPIRY_MS = 30000 // 30 seconds

let isHandlerInitialized = false

interface ReloadAttempt {
  count: number
  timestamp: number
}

// Error message patterns for different browsers
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading \w+ chunk/i,
  /text\/html/i, // Sometimes servers return HTML for missing chunks
]

function getReloadAttempt(): ReloadAttempt | null {
  const stored = sessionStorage.getItem(RELOAD_ATTEMPT_KEY)
  if (!stored) return null

  try {
    const parsed = JSON.parse(stored)

    // Validate structure
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.count !== "number" ||
      typeof parsed.timestamp !== "number"
    ) {
      sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
      return null
    }

    const attempt: ReloadAttempt = {
      count: parsed.count,
      timestamp: parsed.timestamp,
    }

    // Check if attempt has expired
    if (Date.now() - attempt.timestamp > RELOAD_ATTEMPT_EXPIRY_MS) {
      sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
      return null
    }

    return attempt
  } catch {
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
    return null
  }
}

function setReloadAttempt(count: number): void {
  const attempt: ReloadAttempt = {
    count,
    timestamp: Date.now(),
  }
  sessionStorage.setItem(RELOAD_ATTEMPT_KEY, JSON.stringify(attempt))
}

function clearReloadAttempt(): void {
  sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
}

export function canAttemptReload(): boolean {
  const attempt = getReloadAttempt()
  return attempt === null || attempt.count < MAX_RELOAD_ATTEMPTS
}

function incrementReloadAttempt(): number {
  const attempt = getReloadAttempt()
  const newCount = (attempt?.count ?? 0) + 1
  setReloadAttempt(newCount)
  return newCount
}

export function isChunkLoadError(error: Error | string): boolean {
  const message = typeof error === "string" ? error : (error.message ?? "")
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export function setupChunkErrorHandler(): void {
  // Prevent multiple initializations
  if (isHandlerInitialized) {
    return
  }
  isHandlerInitialized = true

  // Clear reload attempts on successful page load
  clearReloadAttempt()

  // Listen for Vite's preload error event
  window.addEventListener("vite:preloadError", (event) => {
    console.warn("[ChunkErrorHandler] vite:preloadError received")

    if (canAttemptReload()) {
      event.preventDefault()
      const attemptNumber = incrementReloadAttempt()
      console.info(
        `[ChunkErrorHandler] Attempting reload (${attemptNumber}/${MAX_RELOAD_ATTEMPTS})...`,
      )
      Sentry.captureMessage("Auto-reloading due to chunk error (preload)", {
        level: "warning",
        extra: { attemptNumber },
      })
      window.location.reload()
    }
  })

  // Handle unhandled promise rejections for chunk errors
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as Error | undefined
    if (reason && isChunkLoadError(reason)) {
      console.warn(
        "[ChunkErrorHandler] Chunk error in unhandled rejection:",
        reason.message,
      )

      if (canAttemptReload()) {
        event.preventDefault()
        const attemptNumber = incrementReloadAttempt()
        console.info(
          `[ChunkErrorHandler] Attempting reload (${attemptNumber}/${MAX_RELOAD_ATTEMPTS})...`,
        )
        Sentry.captureMessage("Auto-reloading due to chunk error (rejection)", {
          level: "warning",
          extra: { attemptNumber, errorMessage: reason.message },
        })
        window.location.reload()
      }
    }
  })
}
